import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import ServiceCategory from 'services/ServiceCategory'
import AlertDialog from 'components/AlertDialog'
import Select from 'components/Inputs/Select'
import TextInput from 'components/Inputs/TextInput'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import BusinessServiceList from 'components/Lists/BusinessServiceList'
import ProfessionalServicesModal from 'pages/Modals/ServicesModal/Professional'
import { ReactComponent as Loading } from 'icons/loading2.svg'

import {
  Container,
  Row,
  Label,
  InfoText,
  LinkText
} from '../styles'

export default function EditProfessionalServices ({
  professional,
  professionalCategories: categories,
  professionalWorkingDays,
  onCategoriesUpdated: handleCategoriesUpdated,
  onWorkingDaysUpdated: handleWorkingDaysUpdated
}) {
  const [loading, setLoading] = useState (true)
  const [submitting, setSubmitting] = useState (false)
  const [weekdays, setWeekdays] = useState (null)
  const [services, setServices] = useState ([])
  const [serviceCategories, setServiceCategories] = useState ([])
  const [professionalCategories, setProfessionalCategories] = useState (categories)
  const [categoryToRemove, setCategoryToRemove] = useState (null)
  const [showServicesModal, setShowServicesModal] = useState (false)
  const [showRemoveDialog, setShowRemoveDialog] = useState (false)
  const [errorMessage, setErrorMessage] = useState (null)
  const [category, setCategory] = useState (null)
  const [name, setName] = useState ('')
  const [orderBy, setOrderBy] = useState ('')
  const hasEditPermission = AuthService.hasPermission ('business_config.professionals', 'edit')
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()
  const location = useLocation ()
  const isTutorial = !!location.state?.isTutorial

  useEffect (() => {
    getData ()
  }, [professional])

  useEffect (() => {
    getServices ()
  }, [category, name])

  useEffect (() => {
    if (!loading) handleCategoriesUpdated (professionalCategories)
    getServices ()
  }, [professionalCategories])

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const getData = async () => {
    try {

      const { data: weekdays } = await cutileiApi.get ('/weekdays')
      const { data: serviceCategories } = await cutileiApi.get (
        '/service_categories', requestConfig
      )

      setWeekdays (weekdays)
      setServiceCategories (serviceCategories)
      await getServices ()
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const getServices = async () => {
    let filters = `?status=active`
    if (name) filters += `&name=${encodeURIComponent (name)}`
    if (category) filters += `&category_id=${category.value}`
    else filters += `&categories=${professionalCategories?.map (pc => pc.id)?.join (',')}`
    if (orderBy) filters += `&order_by=${orderBy}`
    if (!professional?.accepts_cutilei) filters += `&cutilei_services=false`

    try {
      setLoading (true)

      const { data: services } = await cutileiApi.get (
        `/businesses/${businessId}/professionals/${professional?.id}/services${filters}`,
        requestConfig
      )

      setServices (services)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const editServiceCategories = async newCategories => {
    try {
      setSubmitting (true)
      const service_categories = ServiceCategory.getAddedCategory (professionalCategories, newCategories)

      await cutileiApi.post (
        `/businesses/${businessId}/professionals/${professional?.id}/service_categories`,
        {service_categories},
        requestConfig
      )

      setProfessionalCategories (newCategories.map (c => c.value))
    } catch (error) {
      logApiError (error)
    } finally {
      setSubmitting (false)
    }
  }

  const removeServiceCategory = async category => {
    try {
      setSubmitting (true)

      await cutileiApi.delete (
        `/businesses/${businessId}/professionals/${professional?.id}/service_categories/${category.id}`,
        requestConfig
      )
      
      if (category.name.toLowerCase () === 'manicure e pedicure') {
        const editedWorkingDays = professionalWorkingDays.map (day => ({...day, accepts_cutilei: false}))

        await cutileiApi.post (`/businesses/${businessId}/professionals/${professional.id}/working_days`, {
          working_days: editedWorkingDays
        }, requestConfig)

        handleWorkingDaysUpdated (editedWorkingDays)
      }

      setProfessionalCategories (professionalCategories.filter (c => c.id !== category.id))
    } catch (error) {
      logApiError (error)
      setErrorMessage (getApiErrorMessage (error))
    } finally {
      setSubmitting (false)
    }
  }

  const handleServicesAdded = (addedServices, addedCategories) => {
    setServices ([...services, ...addedServices].sort (compareServices))
    setProfessionalCategories (prevCategories => ([
      ...prevCategories.filter (category => !addedCategories.some (c => c.id === category.id)),
      ...addedCategories
    ]))
  }

  const handleServiceDeleted = async deletedService => {
    const updatedServices = services.filter (s => s.id !== deletedService.id)
    const serviceCategory = deletedService.category

    setServices (updatedServices)

    if (!updatedServices.some (s => s.category.id === serviceCategory.id))
      removeServiceCategory (serviceCategory)
  }

  const compareServices = (a, b) => a.number - b.number
  const toggleServicesModal = () => setShowServicesModal (!showServicesModal)
  const toggleRemoveDialog = () => setShowRemoveDialog (!showRemoveDialog)

  return (
    <Container style={{paddingTop: 0}}>
      {isTutorial && (
        <InfoText>
          <LinkText to='/business'>
            {'Clique aqui '}
          </LinkText>
          para voltar para o <b>guia de configuração do salão</b>
        </InfoText>
      )}
      <Label style={{marginBlock: 5}}>
        Categorias de serviço
      </Label>
      <Row>
        <Select
          name='service_categories'
          placeholder='Selecionar categorias...'
          value={professionalCategories.map (c => ({label: c.name, value: c}))} 
          options={serviceCategories
            .filter (c => !professionalCategories.some (selected => selected.id === c.id))
            .map (c => ({ label: c.name, value: c }))
          }
          isMulti={true}
          isDisabled={!hasEditPermission}
          onChange={(_, options) => editServiceCategories (options)}
          onRemoveOption={option => {
            if (services.some (s => s.category.id === option.value.id && !s.cutilei_service)) {
              setErrorMessage ('Para remover esta categoria, remova do profissional os serviços dela primeiro')
            } else {
              setCategoryToRemove (option.value)
              toggleRemoveDialog ()
            }
          }}
          containerStyles={{width: 600}}
        />
      </Row>
      <Row style={{gap: 10, width: 600}}>
        <Select
          name='category'
          placeholder='Categoria...'
          value={category}
          options={professionalCategories.map (category => ({
            label: category.name,
            value: category.id
          }))}
          isClearable={true}
          onChange={(_, option) => setCategory (option)}
        />
        <TextInput
          value={name}
          placeholder='Pesquisar por nome'
          isClearable={true}
          onChange={e => setName (e.target.value)}
          onClear={() => setName ('')}
          inputStyle={{boxShadow: '0px 1px 10px -6px', paddingInline: 12}}
        />
      </Row>
      {hasEditPermission && (
        <SecondaryButton
          label='Adicionar serviços'
          onClick={toggleServicesModal}
          width={170}
          style={{marginBottom: 20}}
        />
      )}
      {loading ? <Loading style={{marginBottom: 20}}/> : (
        <div style={{marginBottom: 15}}>
          <BusinessServiceList
            services={services}
            weekdays={weekdays}
            professionalID={professional?.id}
            onEdit={service => setServices (services.map (s => s.id === service.id ? {...service} : s))}
            onDelete={handleServiceDeleted}
          />
          <ProfessionalServicesModal
            visible={showServicesModal}
            professionalID={professional?.id}
            serviceCategories={serviceCategories}
            professionalCategories={professionalCategories}
            onConfirm={handleServicesAdded}
            onClose={toggleServicesModal}
          />
        </div>
      )}
      {categoryToRemove && (
        <AlertDialog
          visible={showRemoveDialog}
          title='Atenção!'
          message='Deseja realmente remover esta categoria deste profissional?'
          confirmText='Sim'
          cancelText='Não'
          onConfirm={() => removeServiceCategory (categoryToRemove)}
          onClose={toggleRemoveDialog}
          dangerous={true}
        />
      )}
      <AlertDialog
        visible={!!errorMessage}
        title='Atenção!'
        message={errorMessage}
        confirmText='Ok'
        onConfirm={() => setErrorMessage (null)}
        onClose={() => setErrorMessage (null)}
      />
    </Container>
  )
}
